/* eslint-disable react/jsx-props-no-spreading */
// We have to use props spreading because of React Router's history, match and params via props.
import React, {
  useState,
  Suspense,
  useLayoutEffect,
  useEffect,
  useCallback,
} from "react";
import { BrowserRouter } from "react-router-dom";
import { ApolloProvider, useMutation } from "@apollo/client";
import i18n from "i18n";
import moment from "moment";

// Import global css first and foremost before any component
import "./App.less";

// stripe
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

// components
import { Layout, ConfigProvider } from "antd";
import { FullscreenLoading } from "common/loading";
import NavBar from "common/navbar";
import NetworkErrorBoundary from "common/network_error_boundary/NetworkErrorBoundary";
import { isAuthValid, logout as clearAuth } from "utils/auth";

// hooks
import { AppAuthProvider } from "contexts/useAuthContext";
import { AppLogOutProvider } from "contexts/useLogOutContext";
import useAntLocale from "hooks/useAntLocale";
import apolloClient from "utils/networking";
import STUDENT_LOGOUT from "graphql/mutations/StudentLogout";
// import GET_MAINTENANCE_STATUS from "graphql/queries/GetMaintenanceStatus";
import ReactPixel from "react-facebook-pixel";
import { hotjar } from "react-hotjar";
import AppRouter from "./AppRouter";
// import MaintenancePage from "./MaintenancePage";
// Initialize Google Analytics
import "utils/ga";
// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
// const stripePromise = loadStripe(
//   `${process.env.REACT_APP_STRIPE_CLIENT_API_KEY}`
// );
let stripePromise: any;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(
      `${process.env.REACT_APP_STRIPE_CLIENT_API_KEY}`
    );
  }
  return stripePromise;
};

const cleanUp = () => {
  clearAuth();
  apolloClient.clearStore();
};

function App() {
  // return <MaintenancePage />;
  const [deleteToken] = useMutation(STUDENT_LOGOUT, {
    onCompleted: cleanUp,
    onError: cleanUp,
  });
  const [isLoggedIn, setLoggedIn] = useState(() =>
    isAuthValid(localStorage.getItem("auth"))
  );
  // const { data, loading } = useQuery(GET_MAINTENANCE_STATUS);
  const { antLocale, changeAntLocale } = useAntLocale();
  const logOut = useCallback(() => {
    deleteToken();
    setLoggedIn(false);
  }, [deleteToken, setLoggedIn]);

  // facebook pixels
  useEffect(() => {
    const options = {
      autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
      debug: false, // enable logs
    };
    ReactPixel.init("292089691225444", undefined, options);
    ReactPixel.pageView();
    ReactPixel.fbq("track", "PageView");
  });

  // hotjar
  useEffect(() => {
    hotjar.initialize(2691820, 6);
  }, []);

  useEffect(() => {
    i18n.on("languageChanged", (lng) => {
      // Change Moment locale globally
      moment.locale(lng);

      // Change Ant locale globally
      changeAntLocale(lng);
    });
  }, [changeAntLocale]);

  useLayoutEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth") {
        setLoggedIn(isAuthValid(localStorage.getItem("auth")));
      }
    };

    window.addEventListener("storage", onStorage);
  }, []);
  // if (loading) return <p>Loading...</p>;

  return (
    <AppAuthProvider value={isLoggedIn}>
      <AppLogOutProvider value={logOut}>
        <div className="App">
          <ConfigProvider locale={antLocale}>
            <NetworkErrorBoundary>
              <Layout className="layout">
                <NavBar isLoggedIn={isLoggedIn} logOut={logOut} />
                <AppRouter setLoggedIn={setLoggedIn} />
              </Layout>
            </NetworkErrorBoundary>
          </ConfigProvider>
        </div>
      </AppLogOutProvider>
    </AppAuthProvider>
  );
}

function AppConfigure() {
  return (
    <Suspense fallback={FullscreenLoading}>
      <BrowserRouter>
        <ApolloProvider client={apolloClient}>
          <Elements stripe={getStripe()}>
            <App />
          </Elements>
        </ApolloProvider>
      </BrowserRouter>
    </Suspense>
  );
}

export default AppConfigure;
